import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, finalize, tap } from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { InventoryItemSalesPrice } from 'src/app/core/models/inventory/inventory-item-sales-price.interface';
import { OrganizationService } from 'src/app/core/services/organization.service';

@Component({
  selector: 'app-organization-sales-prices-tab',
  templateUrl: './organization-sales-prices-tab.component.html',
  styleUrl: './organization-sales-prices-tab.component.scss',
  standalone: false,
})
export class OrganizationSalesPricesTabComponent implements OnChanges {
  private readonly organizationService = inject(OrganizationService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public organizationId = '';

  public readonly notFound = Constants.NotFound;
  public searchTerm = '';
  public isLoading = false;
  public pageSize = 10;
  public totalItems = 0;
  public readonly salesPrices$ = new BehaviorSubject<InventoryItemSalesPrice[]>(
    [],
  );
  public readonly pagedSalesPrices$ =
    new BehaviorSubject<InventoryItemSalesPrice[]>([]);

  private page = 1;
  private filteredSalesPrices: InventoryItemSalesPrice[] = [];

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['companyId'] || changes['organizationId']) &&
      this.companyId &&
      this.organizationId
    ) {
      this.loadTabData();
    }
  }

  public onSearchChange(value: string): void {
    this.searchTerm = value;
    this.page = 1;
    this.applySearchAndPaging();
  }

  public onPageChange(page: number): void {
    this.page = page;
    this.applySearchAndPaging();
  }

  public onChangePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.applySearchAndPaging();
  }

  private loadTabData(): void {
    this.isLoading = true;
    this.organizationService.setOrganizationsUrl(this.companyId);
    this.organizationService
      .getOrganization(this.organizationId)
      .pipe(
        tap((organization) => {
          this.salesPrices$.next(organization.salesPrices ?? []);
          this.page = 1;
          this.applySearchAndPaging();
        }),
        catchError(() => {
          this.salesPrices$.next([]);
          this.pagedSalesPrices$.next([]);
          this.totalItems = 0;
          return EMPTY;
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();
  }

  private applySearchAndPaging(): void {
    const searchTerm = this.searchTerm.trim().toLowerCase();
    const allSalesPrices = this.salesPrices$.getValue();

    this.filteredSalesPrices = searchTerm.length
      ? allSalesPrices.filter((price) =>
          this.toSearchValue(price).includes(searchTerm),
        )
      : allSalesPrices;

    this.totalItems = this.filteredSalesPrices.length;

    if (this.totalItems === 0) {
      this.pagedSalesPrices$.next([]);
      return;
    }

    const totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    if (this.page > totalPages) {
      this.page = totalPages;
    }

    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.pagedSalesPrices$.next(this.filteredSalesPrices.slice(start, end));
  }

  private toSearchValue(price: InventoryItemSalesPrice): string {
    return [
      price.lineNumber?.toString() ?? '',
      price.startingDate ?? '',
      price.endingDate ?? '',
      price.unitOfMeasureCode ?? '',
      price.currencyCode ?? '',
      price.minimumQuantity?.toString() ?? '',
      price.unitPrice?.toString() ?? '',
      price.lineDiscountPercent?.toString() ?? '',
      price.priceUom?.toString() ?? '',
      price.orderQuantityMultiple?.toString() ?? '',
      price.description ?? '',
      price.assignToNo ?? '',
      price.priceListCode ?? '',
    ]
      .join(' ')
      .toLowerCase();
  }
}

